import React from 'react'
import 'events'
import { Logger } from 'typescript-log'
import { useConsentGiven } from 'common/hooks/useConsentGiven'
import { ConsentLevel } from 'common/data/consent/consent.redux'
import { ConsentFallback } from 'component-library/tracking/ConsentFallback/ConsentFallback'

export interface Props {
    link: string
    log: Logger
}

export interface FanHubMessageEvent extends MessageEvent {
    data: FanHubHeightPayload
}

export interface FanHubHeightPayload {
    type: string
    widget_id: string
    height: number
}

export const fanhubUATstring = 'fanhubmedia.com'
export const fanhubProdString = 'thegame.com.au'

export const isFanHub = (link: string) => {
    return link.indexOf(fanhubUATstring) + link.indexOf(fanhubProdString) >= -1
}

export function extractString(link: string): string {
    const aflmatcher = new RegExp('afl\\d{22}$')
    const aflString = aflmatcher.exec(link)
    return aflString ? aflString[0] : ''
}

// FanHub will also emit a resize event to AMP which is not of interest in this context, so filter on source and data
const isFanHubMessage = (event: FanHubMessageEvent | MessageEvent): event is FanHubMessageEvent => {
    return (
        event.origin.indexOf(fanhubUATstring) + event.origin.indexOf(fanhubProdString) >= -1 &&
        event.data &&
        event.data.toString().indexOf('fgpChangeHeight') >= 0
    )
}

export const FanHub: React.FC<Props> = ({ link, log }) => {
    const id = `swm-polling-321-widget-${extractString(link)}`
    const fanHubWidget = React.useRef<HTMLIFrameElement>(null)

    function handleFanHubMessage(event: FanHubMessageEvent | MessageEvent) {
        if (!isFanHubMessage(event) || !event.data) {
            return
        }
        // do not attempt to handle messages if there's no iframe yet
        if (!fanHubWidget.current) {
            return
        }
        try {
            const payload: FanHubHeightPayload = JSON.parse(event.data.toString())
            if (payload && fanHubWidget.current.height != payload.height.toString()) {
                fanHubWidget.current.height = `` + payload.height.toString()
            }
        } catch (error) {
            log.error({ err: error }, 'FanHub message is not of the expected type')
        }
    }

    React.useEffect(() => {
        const currentWidget = fanHubWidget.current
        if (currentWidget) {
            window.addEventListener('message', handleFanHubMessage)
        }

        return () => {
            if (currentWidget) {
                window.removeEventListener('message', handleFanHubMessage)
            }
        }
    })

    const consentGiven = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Analytics | ConsentLevel.Advertising,
    })

    if (!consentGiven) {
        return <ConsentFallback />
    }

    return (
        <iframe
            id={id}
            width="100%"
            frameBorder={0}
            src={link}
            sandbox="allow-same-origin allow-popups allow-scripts"
            ref={fanHubWidget}
            height="auto"
            className="swm-polling-321-widget"
            scrolling="no"
        />
    )
}
